import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { SCENES, GROUPS } from "../scenes";
import "./SceneMenu.css";

/**
 * The "immersive" control: a trigger that opens a submenu of scenes.
 *
 * Opens on hover for pointers that have one, and on click/Enter for everything
 * else — a hover-only menu is unreachable on touch and by keyboard. Closing is
 * deliberately delayed, because the gap between trigger and panel is otherwise
 * enough to lose the menu mid-reach.
 */
export default function SceneMenu({
  className = "",
  activeScene,
  onSelect,
  align = "left",
  children,
}) {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef(null);
  const rootRef = useRef(null);
  const id = useId();

  const cancelClose = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  const scheduleClose = useCallback(() => {
    cancelClose();
    closeTimer.current = setTimeout(() => setOpen(false), 180);
  }, [cancelClose]);

  useEffect(() => cancelClose, [cancelClose]);

  // Pointer-down outside and Escape both dismiss.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (!rootRef.current?.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const choose = (sceneId) => {
    setOpen(false);
    onSelect(sceneId);
  };

  return (
    <div
      ref={rootRef}
      className={`scene-menu${open ? " is-open" : ""} scene-menu--${align} ${className}`}
      onMouseEnter={() => {
        cancelClose();
        setOpen(true);
      }}
      onMouseLeave={scheduleClose}
    >
      <button
        type="button"
        className="scene-menu__trigger"
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen((o) => !o)}
      >
        {children}
        <svg
          className="scene-menu__caret"
          width="9"
          height="6"
          viewBox="0 0 9 6"
          aria-hidden="true"
        >
          <path
            d="M1 1.5 4.5 5 8 1.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      <div className="scene-menu__panel" id={id} role="menu">
        {GROUPS.map((group) => (
          <div key={group.id} className="scene-menu__group">
            <p className="scene-menu__heading">
              {group.label}
              <span>{group.note}</span>
            </p>
            {SCENES.filter((s) => s.group === group.id).map((scene) => (
              <button
                key={scene.id}
                type="button"
                role="menuitem"
                className={`scene-menu__item${
                  scene.id === activeScene ? " is-active" : ""
                }`}
                onClick={() => choose(scene.id)}
              >
                <span className="scene-menu__item-head">
                  <span className="scene-menu__label">{scene.label}</span>
                  <span className="scene-menu__tag">{scene.tag}</span>
                </span>
                <span className="scene-menu__blurb">{scene.blurb}</span>
              </button>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
